import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import { generateEmailPreview } from '../lib/emailTemplates'
import { useAuth } from '../context/AuthContext'
import ReminderTimeline from '../components/ReminderTimeline'
import StatusBadge from '../components/StatusBadge'
import Card from '../components/Card'
import type { InvoiceRow, ReminderStepRow } from '../types'
import { formatCurrency, formatDate } from '../utils/helpers'

interface InvoiceWithSteps extends InvoiceRow {
  reminder_steps: ReminderStepRow[]
}

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>()
  const api = useApi()
  const { profile } = useAuth()
  const [invoice, setInvoice] = useState<InvoiceWithSteps | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    if (!id) return
    api
      .getInvoice(id)
      .then((data) => setInvoice(data.invoice))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [id])

  async function handleMarkPaid() {
    if (!id || !invoice) return
    setActionLoading(true)
    try {
      await api.markPaid(id)
      setInvoice({
        ...invoice,
        status: 'paid',
        paid_at: new Date().toISOString(),
        reminder_steps: invoice.reminder_steps.map((r) =>
          r.status === 'scheduled' ? { ...r, status: 'skipped' as const } : r
        ),
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark paid')
    } finally {
      setActionLoading(false)
    }
  }

  async function handlePause() {
    if (!id || !invoice) return
    setActionLoading(true)
    try {
      await api.pauseInvoice(id)
      setInvoice({ ...invoice, status: 'paused' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to pause')
    } finally {
      setActionLoading(false)
    }
  }

  async function handleResume() {
    if (!id || !invoice) return
    setActionLoading(true)
    try {
      await api.resumeInvoice(id)
      setInvoice({ ...invoice, status: 'pending' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resume')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-ica-border border-t-ica-accent" />
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <Card className="border-ica-red/20 bg-ica-red/5 p-4 text-sm text-ica-red">
        {error || 'Invoice not found'}
      </Card>
    )
  }

  const nextReminder = invoice.reminder_steps?.find((r) => r.status === 'scheduled')
  const emailPreview = nextReminder ? generateEmailPreview(nextReminder, invoice, profile) : null

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{invoice.invoice_label}</h1>
          <p className="mt-1 text-sm text-ica-text-muted">
            {invoice.client_name} · {invoice.client_email}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {invoice.status !== 'paid' && (
            <>
              <button
                onClick={handleMarkPaid}
                disabled={actionLoading}
                className="rounded-xl bg-ica-green/15 px-4 py-2 text-sm font-semibold text-ica-green transition-opacity hover:opacity-80 disabled:opacity-40"
              >
                Mark paid
              </button>
              {invoice.status === 'paused' ? (
                <button
                  onClick={handleResume}
                  disabled={actionLoading}
                  className="rounded-xl bg-ica-accent px-4 py-2 text-sm font-semibold text-ica-accent-text transition-opacity hover:opacity-90 disabled:opacity-40"
                >
                  Resume
                </button>
              ) : (
                <button
                  onClick={handlePause}
                  disabled={actionLoading}
                  className="rounded-xl border border-ica-border px-4 py-2 text-sm font-medium text-ica-text-secondary transition-colors hover:text-ica-text disabled:opacity-40"
                >
                  Pause
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Summary card */}
          <Card elevated className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-ica-text-muted">Amount</p>
                <p className="mt-1 text-xl font-bold">{formatCurrency(invoice.invoice_amount, invoice.currency)}</p>
              </div>
              <div>
                <p className="text-xs text-ica-text-muted">Due date</p>
                <p className="mt-1 text-xl font-bold">{formatDate(invoice.due_date)}</p>
              </div>
              <div>
                <p className="text-xs text-ica-text-muted">Status</p>
                <div className="mt-1"><StatusBadge status={invoice.status} /></div>
              </div>
              <div>
                <p className="text-xs text-ica-text-muted">Tone</p>
                <p className="mt-1 text-xl font-bold capitalize">{invoice.tone}</p>
              </div>
            </div>
            {invoice.notes && (
              <div className="border-t border-ica-border pt-4">
                <p className="text-xs text-ica-text-muted">Notes</p>
                <p className="mt-1 text-sm">{invoice.notes}</p>
              </div>
            )}
            <div className="border-t border-ica-border pt-4">
              <p className="text-xs text-ica-text-muted">Invoice link</p>
              <a
                href={invoice.invoice_link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 block break-all text-sm text-ica-accent hover:underline"
              >
                {invoice.invoice_link}
              </a>
            </div>
          </Card>

          <ReminderTimeline steps={invoice.reminder_steps || []} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {emailPreview && (
            <Card elevated className="space-y-3">
              <h3 className="text-sm font-medium">Next reminder preview</h3>
              <div className="space-y-2">
                <p className="text-xs text-ica-text-muted">Subject</p>
                <p className="text-sm font-medium">{emailPreview.subject}</p>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-ica-text-muted">Body</p>
                <pre className="whitespace-pre-wrap rounded-xl bg-ica-bg p-3 text-xs text-ica-text-secondary">
                  {emailPreview.body}
                </pre>
              </div>
            </Card>
          )}

          <Card elevated className="space-y-3">
            <h3 className="text-sm font-medium">Email test</h3>
            <p className="text-xs text-ica-text-muted">Send a test email to yourself.</p>
            <TestEmailButton invoiceId={invoice.id} />
          </Card>

          <Card elevated className="space-y-3">
            <h3 className="text-sm font-medium">Sent history</h3>
            <p className="text-xs text-ica-text-muted">Email events will appear here.</p>
          </Card>
        </div>
      </div>
    </div>
  )
}

function TestEmailButton({ invoiceId }: { invoiceId: string }) {
  const api = useApi()
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  async function handleClick() {
    if (loading) return
    setLoading(true)
    setResult(null)
    setErr(null)
    try {
      await api.sendTestEmail(invoiceId)
      setResult('Test email sent. Check your inbox.')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to send')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mt-2 space-y-2">
      {result && <div className="rounded-lg bg-ica-green/10 px-3 py-2 text-xs text-ica-green">{result}</div>}
      {err && <div className="rounded-lg bg-ica-red/10 px-3 py-2 text-xs text-ica-red">{err}</div>}
      <button
        onClick={handleClick}
        disabled={loading}
        className="rounded-lg border border-ica-border px-3 py-1.5 text-xs font-medium text-ica-text-secondary transition-colors hover:text-ica-text disabled:opacity-40"
      >
        {loading ? 'Sending...' : 'Send test email'}
      </button>
    </div>
  )
}
